import {
    Box, Switch, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'
import { ApiGetC2CTradableCryptos, ApiUpdateC2CTradable } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'

const thStyle = {
    backgroundColor: '#241815', color: '#ff6a3d', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const, py: 1.5, borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const, top: 0, zIndex: 2,
}
const tdStyle = { color: '#c0c0c0', py: 1.4, fontSize: 13, whiteSpace: 'nowrap' as const }
const trHover = { transition: 'background 0.15s', '&:hover': { background: '#ff6a3d08' } }

const AssetManagement: React.FC = () => {

    const [cryptos, setCryptos] = useState<any[]>([])
    const [updatingId, setUpdatingId] = useState<string | null>(null)
    const [errorPopup, setErrorPopup] = useState({ open: false, message: '' })
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [total, setTotal] = useState(0)
    const [confirmToggle, setConfirmToggle] = useState<{ open: boolean; id: string | null; current: boolean; name: string }>({
        open: false, id: null, current: false, name: '',
    })

    const fetchCryptos = useCallback(async () => {
        try {
            const res = await ApiGetC2CTradableCryptos(`?page=${page + 1}&limit=${rowsPerPage}`)
            if (res?.status) {
                setCryptos(res.data?.items || [])
                setTotal(res.data?.total || 0)
            }
        } catch (e: any) {
            setErrorPopup({
                open: true,
                message: e?.message || e?.response?.data?.message || e?.[0] || 'Failed to fetch C2C cryptos',
            })
        }
    }, [page, rowsPerPage])

    useEffect(() => { fetchCryptos() }, [fetchCryptos])

    const requestToggle = (id: string, current: boolean, name: string) => {
        setConfirmToggle({ open: true, id, current, name })
    }

    const handleToggle = async (id: string, current: boolean) => {
        setUpdatingId(id)
        setCryptos(prev => prev.map(c => c._id === id ? { ...c, c2cTradable: !current } : c))
        try {
            const res = await ApiUpdateC2CTradable(id, { c2cTradable: !current })
            if (!res?.status) throw new Error(res?.message || 'Update failed')
        } catch (e: any) {
            setCryptos(prev => prev.map(c => c._id === id ? { ...c, c2cTradable: current } : c))
            setErrorPopup({
                open: true,
                message: e?.message || e?.response?.data?.message || 'Failed to update status',
            })
        } finally {
            setUpdatingId(null)
        }
    }

    return (
        <>
            <Box className="bg-dark-color border-10 p-0" sx={{ overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: '70vh' }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                        <TableHead>
                            <TableRow>
                                {['#', 'Logo', 'Name', 'Symbol', 'CMC ID', 'Category', 'Active', 'C2C Tradable'].map(h => (
                                    <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cryptos.length > 0 ? cryptos.map((c, i) => (
                                <TableRow key={c._id} sx={trHover}>
                                    <TableCell sx={tdStyle}>{i + 1}</TableCell>
                                    <TableCell sx={tdStyle}>
                                        {c.logo ? (
                                            <Box
                                                component="img"
                                                src={c.logo}
                                                alt={c.symbol}
                                                sx={{ width: 24, height: 24, borderRadius: '50%' }}
                                            />
                                        ) : (
                                            <Box sx={{
                                                width: 24, height: 24, borderRadius: '50%',
                                                background: '#333', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center',
                                                fontSize: 9, color: '#9a9a9a'
                                            }}>
                                                {c.symbol?.slice(0, 2)}
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#e0e0e0" fontWeight={600}>{c.name}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Box sx={{
                                            display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '5px',
                                            fontSize: 11, fontWeight: 700, color: '#a78bfa',
                                            background: '#a78bfa15', border: '1px solid #a78bfa30',
                                            fontFamily: 'monospace'
                                        }}>
                                            {c.symbol}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={12} color="#7a7a7a">{c.cmc_id}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={12} color="#9a9a9a">{c.category || '—'}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: 11, fontWeight: 700,
                                            color: c.isActive ? '#10b981' : '#9ca3af',
                                            backgroundColor: c.isActive ? '#10b98118' : '#9ca3af18',
                                            border: `1px solid ${c.isActive ? '#10b98150' : '#9ca3af50'}`,
                                        }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: c.isActive ? '#10b981' : '#9ca3af' }} />
                                            {c.isActive ? 'ACTIVE' : 'INACTIVE'}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Tooltip title={c.c2cTradable ? 'Disable C2C trading' : 'Enable C2C trading'} arrow>
                                            <span>
                                                <Switch
                                                    checked={!!c.c2cTradable}
                                                    disabled={updatingId === c._id}
                                                    onChange={() => requestToggle(c._id, !!c.c2cTradable, c.name)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#f2674a' },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#f2674a' },
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <CurrencyExchangeIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>No matched cryptos found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton
                    showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
            <PopupDialog
                open={confirmToggle.open}
                onClose={() => setConfirmToggle({ open: false, id: null, current: false, name: '' })}
                onConfirm={() => {
                    setConfirmToggle({ open: false, id: null, current: false, name: '' })
                    if (confirmToggle.id) handleToggle(confirmToggle.id, confirmToggle.current)
                }}
                variant="deposit"
                title="Confirm C2C Tradable Update"
                message={`You are about to ${confirmToggle.current ? 'disable' : 'enable'} C2C trading for ${confirmToggle.name}.`}
                confirmText={confirmToggle.current ? 'Disable' : 'Enable'}
                cancelText="Cancel"
                details={[
                    { label: 'Asset', value: confirmToggle.name },
                    { label: 'Current Status', value: confirmToggle.current ? 'Enabled' : 'Disabled' },
                    { label: 'New Status', value: confirmToggle.current ? 'Disabled' : 'Enabled' },
                ]}
                confirmationText={confirmToggle.current ? 'DISABLE' : 'ENABLE'}
                confirmationLabel={
                    <>Type <strong>{confirmToggle.current ? 'DISABLE' : 'ENABLE'}</strong> to confirm this change</>
                }
            />
        </>
    )
}

export default AssetManagement

const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}